export interface RollupHandle {
  promise: Promise<void>;
  /** Stop silently, leaving the value wherever it is. */
  cancel(): void;
  /** Snap to the final value and resolve. */
  finish(): void;
}

/**
 * Animated count-up: eases `from` → `to` over `ms`, calling onTick each frame.
 * Always ends with an exact onTick(to) so displays never show a stale value.
 */
export function rollup(
  from: number,
  to: number,
  ms: number,
  onTick: (value: number) => void,
): RollupHandle {
  let raf = 0;
  let done = false;
  let resolveFn!: () => void;
  const promise = new Promise<void>((res) => (resolveFn = res));

  const settle = () => {
    if (done) return;
    done = true;
    cancelAnimationFrame(raf);
    onTick(to);
    resolveFn();
  };

  const start = performance.now();
  const step = (now: number) => {
    if (done) return;
    // Clamp below too: rAF timestamps can predate the performance.now() above.
    const t = Math.max(0, Math.min(1, (now - start) / ms));
    const eased = 1 - Math.pow(1 - t, 3);
    if (t >= 1) return settle();
    onTick(from + (to - from) * eased);
    raf = requestAnimationFrame(step);
  };
  raf = requestAnimationFrame(step);

  return {
    promise,
    cancel() {
      if (done) return;
      done = true;
      cancelAnimationFrame(raf);
      resolveFn();
    },
    finish: settle,
  };
}
